import { useEffect, useMemo, useRef } from 'react';
import { useApolloClient } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';

import { ActivitiesContentType, LANGUAGE_CODE_EN_AU } from 'global/constants';
import {
  interactiveLessonSectionFragment,
  interactiveLessonSubSectionFragment,
} from '../lesson-builder.graphql';
import type { InteractiveLessonSectionFragment } from '../lesson-builder.graphql.generated';
import {
  SectionOverviewMinimumData,
  getSortedSectionIds,
} from '../logic/get-sorted-section-ids';
import { useGetActivitySections } from '../activity-builder-section.hooks';
import { getIdsToPreload } from './activity-present.utils';
import { useGetInteractiveLessonParams } from '../activity-builder.hooks';
import {
  ExtraSectionIdentifiers,
  useActivityBuilderApollo,
} from '../activity-builder.apollo';
import { preloadGifFiles } from '../components/shared/logic/preload-gif-files';
import { preloadPdfFiles } from '../components/shared/logic/preload-pdf-files';
import { preloadRiveFiles } from '../components/shared/logic/preload-rive-files';
import { InquisitiveLanguageCodes } from 'global/graphql/scalars.type';
import { useActivityBuilderPresentContext } from './activity-builder-present.context';

export const useReadSectionFromCache = ({
  sectionId,
  skip,
  language = LANGUAGE_CODE_EN_AU,
  version,
}: {
  sectionId: string;
  skip?: boolean;
  language?: InquisitiveLanguageCodes;
  version?: string | null;
}) => {
  const client = useApolloClient();

  let currentSection = client.readFragment<InteractiveLessonSectionFragment>({
    fragment: interactiveLessonSectionFragment,
    fragmentName: 'InteractiveLessonSectionFragment',
    id: client.cache.identify({
      id: sectionId,
      __typename: 'ActivitiesSection',
      language,
      version,
    }),
  });

  if (currentSection === null) {
    currentSection = client.readFragment<InteractiveLessonSectionFragment>({
      fragment: interactiveLessonSubSectionFragment,
      fragmentName: 'InteractiveLessonSubSection',
      id: client.cache.identify({
        id: sectionId,
        __typename: 'ActivitiesSection',
        language,
        version,
      }),
    });
  }

  return skip ? null : currentSection;
};

export const useActivityPresentPreload = ({
  currentId,
  contentId,
  contentType,
  version,
  sectionsOverview,
  activeSectionId,
  lang,
  isPublic,
}: {
  currentId: string;
  contentId: number;
  contentType: ActivitiesContentType;
  version?: string | null;
  sectionsOverview?: SectionOverviewMinimumData[];
  activeSectionId: string;
  lang: InquisitiveLanguageCodes;
  isPublic?: boolean;
}) => {
  const queried = useRef<string[]>([]);

  const { classId } = useActivityBuilderPresentContext();
  const sectionIds = useMemo(
    () => getSortedSectionIds(sectionsOverview),
    [sectionsOverview]
  );

  const nextIds = useMemo(
    () =>
      getIdsToPreload({
        windowSize: 4,
        sectionIds,
        currentId,
        queriedIds: queried.current,
      }),
    [currentId, sectionIds]
  );

  const { data } = useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: nextIds,
    version,
    isPublic,
    skip: nextIds.length === 0,
    language: lang,
    onCompleted: res => {
      const sections = res.getActivitiesSections;
      if (sections) {
        for (let i = 0; i < sections.length; i++) {
          queried.current.push(sections[i].id);
          preloadGifFiles(sections[i]);
          preloadPdfFiles(sections[i]);
          preloadRiveFiles(sections[i]);
        }
      }
    },
  });

  const currentSection = useReadSectionFromCache({
    sectionId: activeSectionId,
    language: lang,
    version,
  });

  return {
    currentSection,
    sectionsData: data,
  };
};

export const useGetInitialActivityToDisplay = () => {
  const initialised = useRef<boolean>(false);

  const {
    contentId,
    contentType,
    version,
    lang: language,
    isPublic,
  } = useGetInteractiveLessonParams();
  const { classId } = useActivityBuilderPresentContext();

  const [searchParams] = useSearchParams();
  const section = searchParams.get('page');
  const activeSectionId = section || ExtraSectionIdentifiers.Introduction;

  const { setActiveSection } = useActivityBuilderApollo();

  // Load the active section if it is in the URL since this is the first
  // thing the user is going to see
  useGetActivitySections({
    contentId,
    contentType,
    classId,
    sectionIds: [activeSectionId],
    version,
    language,
    isPublic,
    skip:
      !(
        activeSectionId !== ExtraSectionIdentifiers.Introduction &&
        activeSectionId !== ExtraSectionIdentifiers.Conclusion
      ) || initialised.current === true,
    onCompleted: () => {
      initialised.current = true;
    },
  });

  useEffect(() => {
    setActiveSection(activeSectionId);
    //eslint-disable-next-line
  }, []);
};
